import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { sendInviteEmail } from '../../services/emailService'
import { useAuth } from '../../contexts/AuthContext'
import supabase from '../../supabase/supabase'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import toast from 'react-hot-toast'

const { FiX, FiUser, FiMail, FiShield, FiPlus, FiLoader } = FiIcons

const AddUserModal = ({ isOpen, onClose }) => {
  const { profile: adminProfile } = useAuth()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    role: 'user'
  })

  const roles = [
    { value: 'user', label: 'Community Member' },
    { value: 'staff', label: 'Library Staff' },
    { value: 'admin', label: 'System Admin' }
  ]

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    const toastId = toast.loading('Processing invitation...')

    try {
      // 1. Log the invite in the database first
      const { error: dbError } = await supabase
        .from('invites')
        .insert([{
          email: formData.email,
          full_name: formData.fullName,
          role: formData.role,
          invited_by: adminProfile.id
        }])

      if (dbError) {
        if (dbError.code === '23505') {
          throw new Error('An invitation for this email has already been sent.')
        }
        throw new Error('Failed to record invite: ' + dbError.message)
      }

      // 2. Send the actual email via the Edge Function
      const emailResult = await sendInviteEmail(
        formData.email,
        formData.fullName,
        formData.role
      )

      if (!emailResult.success) {
        // If email fails, we keep the DB record but warn the user
        toast.error(`Invite saved, but email failed: ${emailResult.error}`, { id: toastId, duration: 5000 })
      } else {
        toast.success(`${formData.fullName} has been invited!`, { id: toastId })
      }

      onClose()
      setFormData({ fullName: '', email: '', role: 'user' })
    } catch (error) {
      toast.error(error.message, { id: toastId })
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-space_indigo/80 backdrop-blur-sm"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white w-full max-w-md rounded-[2rem] shadow-2xl relative z-10 overflow-hidden border-2 border-white-400"
        >
          <div className="px-6 py-4 border-b-2 border-white-400 bg-white-500 flex items-center justify-between">
            <h2 className="text-lg font-black text-space_indigo uppercase tracking-tight flex items-center">
              <div className="bg-hot_berry p-2 rounded-xl mr-3">
                <SafeIcon icon={FiPlus} className="w-4 h-4 text-white" />
              </div>
              Official Invite
            </h2>
            <button onClick={onClose} className="p-2 text-white-300 hover:text-hot_berry transition-colors">
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            <div className="bg-dark_cyan-900 border-2 border-dark_cyan-800 p-4 rounded-2xl">
              <p className="text-[10px] font-black text-dark_cyan uppercase tracking-widest leading-relaxed text-center">
                Invited members will receive an email via Postmark with instructions to access their account.
              </p>
            </div>

            <div>
              <label className="block text-[10px] font-black text-white-300 uppercase tracking-widest mb-2 ml-1">Full Name</label>
              <div className="relative">
                <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-white-400 w-4 h-4" />
                <input
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full pl-11 pr-4 py-3 bg-white-500 border-2 border-white-400 rounded-xl focus:border-dark_cyan transition-all font-bold text-xs"
                  placeholder="Jane Doe"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black text-white-300 uppercase tracking-widest mb-2 ml-1">Email Address</label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-white-400 w-4 h-4" />
                <input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-11 pr-4 py-3 bg-white-500 border-2 border-white-400 rounded-xl focus:border-dark_cyan transition-all font-bold text-xs"
                  placeholder="jane@example.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black text-white-300 uppercase tracking-widest mb-2 ml-1">Access Level</label>
              <div className="grid grid-cols-1 gap-2">
                {roles.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, role: r.value })}
                    className={`flex items-center justify-between p-3 rounded-xl border-2 transition-all ${
                      formData.role === r.value
                        ? 'border-dark_cyan bg-dark_cyan-900 text-dark_cyan'
                        : 'border-white-400 text-white-300 hover:border-white-200'
                    }`}
                  >
                    <div className="flex items-center">
                      <SafeIcon icon={FiShield} className="w-3.5 h-3.5 mr-3" />
                      <span className="text-[10px] font-black uppercase tracking-widest">{r.label}</span>
                    </div>
                    {formData.role === r.value && (
                      <div className="w-2 h-2 bg-dark_cyan rounded-full" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-space_indigo text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-lg shadow-space_indigo/20 hover:bg-space_indigo-400 disabled:opacity-50 transition-all flex items-center justify-center"
            >
              {loading ? <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" /> : 'Dispatch Invitation'}
            </button>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default AddUserModal